import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CallRecording } from '@adornis/voice-assistant/call-recordings.js';
import { NEVER, startWith } from 'rxjs';
import { CallSession } from './CallSession.js';

/**
 * 🆕 B1 2026-09-04 - call-origin data for the /calls overview.
 *
 * The overview table is built over the ENGINE class Call (callID, startedAt, numLogs); the origin
 * columns live in OUR call_sessions. The two join on callID === sessionID - measured 32/32 on dev
 * and 57/57 on production, while callUuid is dead (0/57). One query per page render, then a Map
 * lookup per row, so the table never issues a query per line.
 */
export const listCallOrigins = registerQuery({
  type: () => [CallSession],
  operationName: 'listCallOrigins',
  resolve: (limit: number) => async () => {
    const coll = await getCollection(CallSession);
    const cap = Math.min(Math.max(1, limit ?? 200), 1000);
    const docs = await coll.find({}).sort({ startedAt: -1 }).limit(cap).toArray();
    return docs as unknown as CallSession[];
  },
  params: [{ name: 'limit', type: () => Int }],
});

// ─── Hotfix 16/09/2026 - the /calls list without call-logs ──────────────────────────────────────────
// The engine list (subscribeCalls) kept a watchQuery over the WHOLE call-logs collection and counted
// the logs of every visible row. With one /calls tab open, production node ran at 100% CPU.
// The list now reads OUR call_sessions (one row per call, sessionID === callID) and, only for calls
// that never got a session row, call-recordings. Every read is bounded; call-logs is never touched.

/** The overview pages through the newest calls only. */
export const CALL_OVERVIEW_MAX_ROWS = 500;
/** Extra rows per read, so dropping a duplicate sessionID never ends a page early. */
const CALL_OVERVIEW_OVERFETCH = 20;
/** A call with a session row also has 1-2 recordings, so the scan for recording-only calls reads further. */
const CALL_OVERVIEW_RECORDING_FACTOR = 3;
const CALL_OVERVIEW_SEARCH_MAX = 128;
const OVERVIEW_SESSION_PROJECTION = {
  _id: 1,
  _class: 1,
  sessionID: 1,
  startedAt: 1,
  endedAt: 1,
  callOrigin: 1,
  carrierPhoneNumber: 1,
  spokenPhoneNumber: 1,
  outcome: 1,
};

type OverviewRow = {
  _id: unknown;
  _class?: string;
  sessionID: string;
  startedAt?: Date | null;
  endedAt?: Date | null;
};

// Number(new Number(20)) === 20, and NaN/null/undefined become 0.
const toInt = (value: unknown): number => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) ? n : 0;
};
// baseql 19 hands String arguments over as BOXED String objects (from another realm in the tests), so no typeof.
const toText = (value: unknown): string =>
  Object.prototype.toString.call(value) === '[object String]' ? String(value) : '';
// The same escaping AdornisFilter.getMongoFilter uses: the search text is matched literally.
const escapeSearch = (text: string): string => text.replace(/([^a-zA-Z0-9\s])/g, '\\$1');
const sortTime = (row: OverviewRow): number => new Date(row.startedAt ?? row.endedAt ?? 0).getTime() || 0;

export function callOverviewPeriodCutoff(value: unknown, now = new Date()): Date | null {
  const period = value == null ? 'all' : toText(value);
  const days: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };
  if (period === 'all') return null;
  if (!Object.prototype.hasOwnProperty.call(days, period)) throw new Error('CALL_OVERVIEW_INVALID_PERIOD');
  return new Date(now.getTime() - days[period]! * 24 * 60 * 60 * 1000);
}
export function callOverviewSearchPattern(value: unknown): { $regex: string; $options: string } | null {
  const search = toText(value).trim().slice(0, CALL_OVERVIEW_SEARCH_MAX);
  return search ? { $regex: escapeSearch(search), $options: 'i' } : null;
}
export function callOverviewSessionFilter(
  pattern: ReturnType<typeof callOverviewSearchPattern>,
  cutoff: Date | null,
): Record<string, unknown> {
  const search = pattern ? { sessionID: pattern } : {};
  if (!cutoff) return search;
  const date = { $or: [{ startedAt: { $gte: cutoff } }, { startedAt: null, endedAt: { $gte: cutoff } }] };
  return pattern ? { $and: [search, date] } : date;
}
export function callOverviewRecordingFilter(
  pattern: ReturnType<typeof callOverviewSearchPattern>,
  cutoff: Date | null,
): Record<string, unknown> {
  return { ...(pattern ? { callID: pattern } : {}), ...(cutoff ? { createdAt: { $gte: cutoff } } : {}) };
}

export const listCallOverview = registerQuery({
  type: () => [CallSession],
  operationName: 'listCallOverview',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'period', type: () => String },
  ],
  resolve: (skipArg: number, limitArg: number, filter: AdornisFilter, period?: string) => async () => {
    const skip = Math.max(0, toInt(skipArg));
    const limit = toInt(limitArg);
    // The DataTable probes an operation with limit 0, and Mongo reads limit(0) as "no limit".
    if (limit <= 0 || skip >= CALL_OVERVIEW_MAX_ROWS) return [];
    const end = Math.min(skip + limit, CALL_OVERVIEW_MAX_ROWS);
    const readLimit = end + CALL_OVERVIEW_OVERFETCH;
    const cutoff = callOverviewPeriodCutoff(period);
    const pattern = callOverviewSearchPattern(filter?.search);

    const [sessionsColl, recordingsColl] = await Promise.all([
      getCollection(CallSession),
      getCollection(CallRecording),
    ]);
    const [sessions, recordings] = await Promise.all([
      sessionsColl
        .find(callOverviewSessionFilter(pattern, cutoff), { projection: OVERVIEW_SESSION_PROJECTION, maxTimeMS: 5000 })
        .sort({ startedAt: -1, _id: -1 })
        .limit(readLimit)
        .toArray(),
      recordingsColl
        .find(callOverviewRecordingFilter(pattern, cutoff), {
          projection: { _id: 1, callID: 1, createdAt: 1 },
          maxTimeMS: 5000,
        })
        .sort({ createdAt: -1, _id: -1 })
        .limit(readLimit * CALL_OVERVIEW_RECORDING_FACTOR)
        .toArray(),
    ]);

    const rows = new Map<string, OverviewRow>();
    for (const session of sessions as any[]) {
      // Sorted newest first, so the newest row of a duplicated sessionID wins.
      if (typeof session.sessionID === 'string' && session.sessionID && !rows.has(session.sessionID)) {
        rows.set(session.sessionID, session);
      }
    }

    // Calls without a session row (builds before 10/09 skip calls with zero LLM turns) still have a recording.
    // A session row outside the read limit must not turn its call into a second, recording-only row.
    const orphanIDs = [
      ...new Set(
        (recordings as any[])
          .map(recording => recording.callID)
          .filter((id): id is string => typeof id === 'string' && !!id && !rows.has(id)),
      ),
    ];
    const withSession = new Set<string>();
    if (orphanIDs.length) {
      const found = await sessionsColl
        .find({ sessionID: { $in: orphanIDs } }, { projection: { _id: 0, sessionID: 1 }, maxTimeMS: 5000 })
        .limit(orphanIDs.length * 2 + CALL_OVERVIEW_OVERFETCH)
        .toArray();
      for (const row of found as any[]) if (typeof row.sessionID === 'string') withSession.add(row.sessionID);
    }
    for (const recording of recordings as any[]) {
      const callID = recording.callID;
      if (typeof callID !== 'string' || !callID || rows.has(callID) || withSession.has(callID)) continue;
      // The recording is written near the END of the call, so it can only stand in for the end time.
      rows.set(callID, {
        _id: `recording:${callID}`,
        _class: 'CallSession',
        sessionID: callID,
        startedAt: null,
        endedAt: recording.createdAt ?? null,
      });
    }

    return [...rows.values()]
      .sort((a, b) => sortTime(b) - sortTime(a) || (a.sessionID < b.sessionID ? 1 : a.sessionID > b.sessionID ? -1 : 0))
      .slice(skip, end) as unknown as CallSession[];
  },
});

/**
 * Browser tabs opened before this release keep their OLD client bundle, which subscribes to the engine's
 * subscribeCalls (a watchQuery over the whole call-logs collection, plus a count per row). They reconnect
 * within seconds after a restart, so without this the CPU stays at 100% until every tester reloads.
 * Stale tabs now get one empty list and an open, idle subscription, so they have nothing to retry.
 * resolve is read on every request (baseql server/schema/resolver.js); call this before the schema is built.
 */
export function retireEngineCallListSubscription(): boolean {
  const operation = baseqlMetaData.subscriptions.subscribeCalls;
  if (!operation) return false;
  operation.options.resolve = () => () => NEVER.pipe(startWith([]));
  return true;
}
